import { apiOrigin, currentBearerToken } from './trpc.js'

/**
 * The URL of an attachment's bytes, or null when there are none yet.
 *
 * `checksum` is the readiness flag, and it is one the transfer phase sets only
 * after the bytes are safely in storage. During the dual run most of the library
 * is unfetched, so "no URL" is the ordinary case and callers draw a placeholder
 * rather than a broken image.
 *
 * On the web build this is a same-origin path and the session cookie
 * authenticates it. In the Capacitor build there is no first-party cookie and an
 * `<img>` cannot send an Authorization header, so the token rides in the query
 * — the one place we accept that, and the reason `readSessionToken` takes an
 * opt-in flag rather than always looking there.
 */
export function fileUrl(file: { id: string; checksum: string | null }): string | null {
  if (!file.checksum) return null

  const token = currentBearerToken()
  const query = token ? `?token=${encodeURIComponent(token)}` : ''

  return `${apiOrigin}/files/${file.id}${query}`
}

/** The same file, asked for as a download rather than shown in the browser. */
export function downloadUrl(file: { id: string; checksum: string | null }): string | null {
  const url = fileUrl(file)
  return url ? asDownload(url) : null
}

/** A file URL from `fileUrl`, asked for as a download. */
export function asDownload(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}download=1`
}

/**
 * Saves a file without leaving the page. For a download asked for after a
 * round trip, where a new tab would be a popup Safari blocks without a word:
 * the server answers `download=1` as an attachment, so following a link to
 * it saves the file and the page stays where it is.
 */
export function saveFile(url: string): void {
  const link = document.createElement('a')
  link.href = url.includes('download=1') ? url : asDownload(url)
  link.rel = 'noopener'
  document.body.append(link)
  link.click()
  link.remove()
}

/** JPG, PDF, HEIC — the bit a person recognises, not the full mime type. */
export function fileKindLabel(filename: string, mimeType: string | null | undefined): string {
  const extension = /\.([a-z0-9]{1,5})$/i.exec(filename)?.[1]
  return (extension ?? mimeType?.split('/').at(-1) ?? 'file').toUpperCase().slice(0, 4)
}

/** 812 B, 64 KB, 3.2 MB. Null when the size is not known. */
export function fileSize(bytes: number | null | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes)) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** The filename without its extension, which is what a photo is captioned. */
export function filenameCaption(filename: string): string {
  return filename.replace(/\.[^.]+$/, '')
}

/** What a file is to the screen: something to look at, play, listen to or read. */
export type FileMedia = 'image' | 'video' | 'audio' | 'pdf' | 'other'

/**
 * Read from the attachment's kind first and its mime type second, because an
 * imported photo often arrives as `application/octet-stream`. A picture
 * message is a picture unless its type says it is a clip.
 */
export function fileMedia(file: { filename: string; mimeType?: string | null; kind?: string | null }): FileMedia {
  const mime = (file.mimeType ?? '').toLowerCase()
  if (file.kind === 'PHOTO' || file.kind === 'SIGNATURE' || mime.startsWith('image/')) return 'image'
  if (file.kind === 'VIDEO' || mime.startsWith('video/')) return 'video'
  if (
    file.kind === 'AUDIO' ||
    file.kind === 'CALL_RECORDING' ||
    file.kind === 'VOICEMAIL' ||
    mime.startsWith('audio/')
  ) {
    return 'audio'
  }
  if (file.kind === 'MMS_MEDIA') return 'image'
  if (mime === 'application/pdf' || /\.pdf$/i.test(file.filename)) return 'pdf'
  // An untyped upload still says what it is in its name.
  if (/\.(jpe?g|png|gif|webp|heic|heif|avif)$/i.test(file.filename)) return 'image'
  if (/\.(mp4|mov|m4v|webm|3gp)$/i.test(file.filename)) return 'video'
  if (/\.(m4a|mp3|wav|aac|ogg|oga|webm)$/i.test(file.filename)) return 'audio'
  return 'other'
}

/**
 * The business's logo, which `POST /files/logo` writes onto Settings rather
 * than onto a job. PNG or JPEG: the PDF embeds nothing else. Decision 81.
 */
export async function uploadLogo(file: File | Blob): Promise<{ logoUrl: string }> {
  const body = new FormData()
  body.append('file', file, file instanceof File ? file.name : 'logo.png')
  const token = currentBearerToken()

  const response = await fetch(`${apiOrigin}/files/logo`, {
    method: 'POST',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
    body,
  })

  if (!response.ok) {
    const reason = await response
      .json()
      .then((payload: { error?: string }) => payload.error)
      .catch(() => null)
    throw new Error(reason ?? `The logo did not upload (${response.status}).`)
  }

  return (await response.json()) as { logoUrl: string }
}

export async function removeLogo(): Promise<void> {
  const token = currentBearerToken()
  const response = await fetch(`${apiOrigin}/files/logo`, {
    method: 'DELETE',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
  })
  if (!response.ok) throw new Error(`The logo was not removed (${response.status}).`)
}

/** The logo's public path, made absolute for the native build. */
export function logoSrc(logoUrl: string | null | undefined): string | null {
  return logoUrl ? `${apiOrigin}${logoUrl}` : null
}

export type UploadedFile = {
  id: string
  kind: string
  filename: string
  mimeType: string
  sizeBytes: number
  checksum: string | null
}

/**
 * Sends one file to `POST /files` and hands back the `Attachment` it became.
 *
 * Not a tRPC mutation, and not for want of trying: tRPC serialises its input as
 * JSON, so a photo would have to be base64 in a request body — a third more
 * bytes over the connection that least affords them, and the whole 12 MB of it
 * buffered as a string on both sides. `fetch` with a `FormData` streams.
 *
 * Credentials go the same two ways as the download: the cookie on the web build,
 * the bearer token in the native one.
 */
export async function uploadFile(
  file: File | Blob,
  options: {
    /**
     * The job it belongs to, or `scope: 'licence'` for a photographed card, or
     * `scope: 'proposal'` for a photograph in a proposal template.
     */
    jobId?: string
    scope?: 'licence' | 'proposal'
    filename?: string
    kind?: 'PHOTO' | 'SIGNATURE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO'
  },
): Promise<UploadedFile> {
  const body = new FormData()
  if (options.jobId) body.append('jobId', options.jobId)
  if (options.scope) body.append('scope', options.scope)
  if (options.kind) body.append('kind', options.kind)
  body.append('file', file, options.filename ?? (file instanceof File ? file.name : 'upload'))

  const token = currentBearerToken()

  const response = await fetch(`${apiOrigin}/files`, {
    method: 'POST',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
    body,
  })

  if (!response.ok) {
    const reason = await response
      .json()
      .then((payload: { error?: string }) => payload.error)
      .catch(() => null)

    throw new Error(reason ?? `That file did not upload (${response.status}).`)
  }

  return (await response.json()) as UploadedFile
}

/**
 * A staff member's photo, for the customer's booking page. Uploads the moment
 * it is picked, like the logo: it is a file, not a field. Decision 104.
 */
export async function uploadPersonPhoto(userId: string, file: File | Blob): Promise<{ photoUrl: string }> {
  const body = new FormData()
  body.append('file', file, file instanceof File ? file.name : 'photo.jpg')
  const token = currentBearerToken()

  const response = await fetch(`${apiOrigin}/files/people/${encodeURIComponent(userId)}/photo`, {
    method: 'POST',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
    body,
  })

  if (!response.ok) {
    const reason = await response
      .json()
      .then((payload: { error?: string }) => payload.error)
      .catch(() => null)
    throw new Error(reason ?? `The photo did not upload (${response.status}).`)
  }

  return (await response.json()) as { photoUrl: string }
}

export async function removePersonPhoto(userId: string): Promise<void> {
  const token = currentBearerToken()
  const response = await fetch(`${apiOrigin}/files/people/${encodeURIComponent(userId)}/photo`, {
    method: 'DELETE',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
  })
  if (!response.ok) throw new Error(`The photo was not removed (${response.status}).`)
}
